import { Badge, Group, Loader, Modal, Stack, Table, Text, Title } from '@mantine/core'
import { useQuery } from 'react-query'
import { useTranslation } from 'react-i18next'

import { fetchRunDetail } from '../service/adminConnectionsService'
import { ACTIVE_RUN_STATUSES, RosterSyncRunDetail } from '../types'

const RUN_POLL_INTERVAL_MS = 5000

interface Props {
  runId: string | null
  onClose: () => void
}

/** Splits a camelized counter key into readable words (schoolsCreated -> schools created). */
const humanizeCounterKey = (key: string) =>
  key.replace(/([A-Z])/g, ' $1').toLowerCase().trim()

const RunDetailModal = ({ runId, onClose }: Props) => {
  const { t } = useTranslation()

  const { data: run, isLoading } = useQuery<RosterSyncRunDetail>(
    ['admin-connections', 'run-detail', runId],
    () => fetchRunDetail(runId as string),
    {
      enabled: Boolean(runId),
      refetchOnWindowFocus: false,
      retry: 1,
      refetchInterval: detail =>
        detail && ACTIVE_RUN_STATUSES.includes(detail.status) ? RUN_POLL_INTERVAL_MS : false,
    },
  )

  const counterEntries = Object.entries(run?.counters ?? {})
    .filter(([, count]) => count > 0)
    .sort(([a], [b]) => a.localeCompare(b))

  return (
    <Modal
      opened={runId !== null}
      onClose={onClose}
      title={<Title order={4}>{t('admin_connections.run_detail_title')}</Title>}
      size='lg'
    >
      {isLoading || !run ? (
        <Group position='center' py='xl'>
          <Loader />
        </Group>
      ) : (
        <Stack spacing='md'>
          <Group spacing='xs'>
            <Badge>{t(`admin_connections.status.${run.status}`)}</Badge>
            <Badge variant='outline'>
              {run.dryRun ? t('admin_connections.dry_run') : t('admin_connections.full_sync')}
            </Badge>
            <Text size='sm' color='dimmed'>
              {run.startedAt ? new Date(run.startedAt).toLocaleString() : '—'}
              {run.finishedAt ? ` → ${new Date(run.finishedAt).toLocaleString()}` : ''}
            </Text>
          </Group>

          <Title order={5}>{t('admin_connections.counters_title')}</Title>
          {counterEntries.length === 0 ? (
            <Text size='sm' color='dimmed'>
              {t('admin_connections.counters_empty')}
            </Text>
          ) : (
            <Table striped>
              <tbody>
                {counterEntries.map(([key, count]) => (
                  <tr key={key}>
                    <td>{humanizeCounterKey(key)}</td>
                    <td align='right'>
                      <Text weight={600}>{count}</Text>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}

          <Title order={5}>{t('admin_connections.failures_title')}</Title>
          {run.failures.length === 0 ? (
            <Text size='sm' color='dimmed'>
              {t('admin_connections.failures_empty')}
            </Text>
          ) : (
            <>
              {run.failuresTotal > run.failures.length && (
                <Text size='xs' color='dimmed'>
                  {t('admin_connections.failures_shown', {
                    shown: run.failures.length,
                    total: run.failuresTotal,
                  })}
                </Text>
              )}
              <Table striped>
                <thead>
                  <tr>
                    <th>{t('admin_connections.failure_severity')}</th>
                    <th>{t('admin_connections.failure_record')}</th>
                    <th>{t('admin_connections.failure_reason')}</th>
                  </tr>
                </thead>
                <tbody>
                  {run.failures.map((failure, index) => (
                    <tr key={`${failure.reasonCode}-${failure.externalId ?? index}`}>
                      <td>
                        <Badge color={failure.severity === 'error' ? 'red' : 'yellow'} size='sm'>
                          {failure.severity}
                        </Badge>
                      </td>
                      <td>
                        <Text size='sm'>
                          {failure.recordType}
                          {failure.externalId ? ` · ${failure.externalId}` : ''}
                        </Text>
                      </td>
                      <td>
                        <Text size='sm'>{failure.reasonCode}</Text>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </>
          )}
        </Stack>
      )}
    </Modal>
  )
}

export default RunDetailModal
